import { useLazyQuery, useMutation } from "@apollo/client";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  Button,
  Card,
  FlatActionList,
  Label,
  Page,
  Stack,
  TextField,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import {
  MfaCodeStrategy,
  Remove2faStrategyResult,
  Staff,
} from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Switch } from "react-native";
import { Colors, View } from "react-native-ui-lib";
import { SettingsManageUsersRootParamList } from "../../../../types";
import { Remove2faStrategyForStaffMutation } from "../../../../utilities/2fa";
import { useSetupAllStartupEntities } from "../../../../utilities/setup";
import {
  MUTATION_UPDATE_STAFF_USER_ACCOUNT,
  QUERY_STAFF_USER,
} from "../../common";
import { StaffUserForm, StaffUserFormValues } from "../../components";
import { useTranslation } from "../../../../utilities/i18n";

export type SettingsEditStaffMemberScreenProps = NativeStackScreenProps<
  SettingsManageUsersRootParamList,
  "SettingsEditStaffMemberScreen"
>;

type ContentProps = SettingsEditStaffMemberScreenProps & {
  cardSlot?: React.ReactNode;
};

type Removing2faMethodsMap = Map<MfaCodeStrategy, Remove2faMethodState>;

enum Remove2faMethodState {
  Loading,
  Removed,
}

const Content = ({ navigation, cardSlot }: ContentProps) => {
  const { control, handleSubmit, formState } =
    useFormContext<StaffUserFormValues>();

  const { fetchAndSyncEntity } = useSetupAllStartupEntities();

  const [mutationUpdateStaffMember] = useMutation<{
    updateStaffUserAccount: Staff;
  }>(MUTATION_UPDATE_STAFF_USER_ACCOUNT);

  const flashMessagesContext = useFlashMessages();

  const { t } = useTranslation("settings");

  const { isDirty, isLoading, isSubmitting, isValid, isSubmitSuccessful } =
    formState;

  const disabled =
    !isDirty || isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  const handleOnSubmit = async (data: StaffUserFormValues) => {
    try {
      const { id, fullName, initials, emailAddress, username, newPassword } =
        data;
      const response = await mutationUpdateStaffMember({
        variables: {
          input: {
            staffId: id,
            fullName,
            initials,
            emailAddress,
            username,
            newPassword: newPassword,
            enabled: data.enabled,
          },
        },
      });

      if (response.data?.updateStaffUserAccount) {
        flashMessagesContext.showMessage({
          severity: "success",
          message: t("message.staff_member_updated"),
        });
        await fetchAndSyncEntity("staffs");
        navigation.goBack();
      }
    } catch (error: any) {
      console.log(error);
      flashMessagesContext.showMessage({
        severity: "error",
        message: error?.message ?? t("message.error_updating_staff_member"),
      });
      return;
    }
  };

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.save_staff")}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page
      title="Edit user"
      secondaryTitle="Edit staff user details"
      scrollable={true}
      footer={footerMarkup}
    >
      <Stack vertical>
        <Card title="Details">
          <Stack vertical>
            <View>
              <Label required label={t("form.staff_fullname.label")}></Label>
              <Controller
                name="fullName"
                control={control}
                render={({
                  field: { onChange, value, onBlur },
                  fieldState: { error },
                }) => (
                  <TextField
                    onBlur={onBlur}
                    value={value ?? ""}
                    onChangeText={onChange}
                    validationMessage={error ? error.message : undefined}
                    placeholder={t("form.staff_fullname.placeholder")}
                  />
                )}
              />
            </View>
            <View>
              <Label required label={t("form.staff_initials.label")}></Label>
              <Controller
                name="initials"
                control={control}
                render={({
                  field: { onChange, value, onBlur },
                  fieldState: { error },
                }) => (
                  <TextField
                    onBlur={onBlur}
                    value={value ?? ""}
                    onChangeText={onChange}
                    validationMessage={error ? error.message : undefined}
                    placeholder={t("form.staff_initials.placeholder")}
                  />
                )}
              />
            </View>
            <View>
              <Label label={t("form.staff_email.label")}></Label>
              <Controller
                name="emailAddress"
                control={control}
                render={({
                  field: { onChange, value, onBlur },
                  fieldState: { error },
                }) => (
                  <TextField
                    onBlur={onBlur}
                    validate="email"
                    value={value ?? ""}
                    onChangeText={onChange}
                    validationMessage={error ? error.message : undefined}
                    placeholder={t("form.staff_email.placeholder")}
                  />
                )}
              />
            </View>
          </Stack>
        </Card>
        <Card title="credentials">
          <Stack vertical>
            <View>
              <Label required label={t("form.staff_username.label")}></Label>
              <Controller
                name="username"
                control={control}
                render={({
                  field: { onChange, value, onBlur },
                  fieldState: { error },
                }) => (
                  <TextField
                    onBlur={onBlur}
                    value={value ?? ""}
                    onChangeText={onChange}
                    validationMessage={error ? error.message : undefined}
                    placeholder={t("form.staff_username.placeholder")}
                  />
                )}
              />
            </View>
            <View>
              <Label required label={t("form.staff_password.label")}></Label>
              <Controller
                name="newPassword"
                control={control}
                render={({
                  field: { onChange, value, onBlur },
                  fieldState: { error },
                }) => (
                  <TextField
                    secureTextEntry
                    onBlur={onBlur}
                    value={value ?? ""}
                    onChangeText={onChange}
                    validationMessage={error ? error.message : undefined}
                    placeholder={t("form.staff_password.placeholder")}
                  />
                )}
              />
            </View>
            <View>
              <Label
                required
                label={t("form.staff_confirm_password.label")}
              ></Label>
              <Controller
                name="confirmPassword"
                control={control}
                render={({
                  field: { onChange, value, onBlur },
                  fieldState: { error },
                }) => (
                  <TextField
                    secureTextEntry
                    onBlur={onBlur}
                    value={value ?? ""}
                    onChangeText={onChange}
                    validationMessage={error ? error.message : undefined}
                    placeholder={t("form.staff_confirm_password.placeholder")}
                  />
                )}
              />
            </View>
          </Stack>
        </Card>

        {cardSlot}

        <Card title="other">
          <Stack vertical>
            <FlatActionList
              items={[
                {
                  key: "staff_enabled",
                  title: t("form.staff_enabled.label"),
                  subTitle: t("form.staff_enabled.description"),
                  onPress: () => {},
                  componentRight: () => (
                    <Controller
                      name="enabled"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <View>
                          <Switch
                            value={value}
                            onValueChange={() => {
                              onChange(!value);
                            }}
                          />
                        </View>
                      )}
                    />
                  ),
                },
              ]}
            />
          </Stack>
        </Card>
      </Stack>
    </Page>
  );
};

export const SettingsEditStaffMemberScreen = ({
  ...props
}: SettingsEditStaffMemberScreenProps) => {
  const { route } = props;

  const { t } = useTranslation("settings");

  const [removing2faMethodsState, updateRemoving2faMethodsState] =
    React.useState<Map<MfaCodeStrategy, Remove2faMethodState>>(new Map());

  const flashMessagesContext = useFlashMessages();

  const [query] = useLazyQuery<{
    staff: Staff;
  }>(QUERY_STAFF_USER);

  const [remove2faStrategyForStaff] = useMutation<{
    remove2faStrategyForStaff: Remove2faStrategyResult;
  }>(Remove2faStrategyForStaffMutation);

  // Replace with the actual query to fetch staff details
  const { staffId } = route.params;
  const [staffUser, setStaffUser] = React.useState<Staff | null>(null);

  async function handleRemove2faMethodForStaffUser(strategy: MfaCodeStrategy) {
    if (!staffUser || removing2faMethodsState.has(strategy)) {
      return;
    }

    updateRemoving2faMethodsState((prev: Removing2faMethodsMap) => {
      const next = new Map(prev); // <-- clone to create a new reference
      next.set(strategy, Remove2faMethodState.Loading);
      return next as Removing2faMethodsMap;
    });

    return remove2faStrategyForStaff({
      variables: {
        userId: staffUser.userId,
        strategy,
      },
    })
      .then(() => {
        updateRemoving2faMethodsState((prev: Removing2faMethodsMap) => {
          const next = new Map(prev); // <-- clone to create a new reference
          next.set(strategy, Remove2faMethodState.Removed);
          return next as Removing2faMethodsMap;
        });
        flashMessagesContext.showMessage({
          severity: "success",
          message: t("messages.removed_2fa_method.message"),
        });
      })
      .catch((error) => {
        updateRemoving2faMethodsState((prev: Removing2faMethodsMap) => {
          const next = new Map(prev); // <-- clone to create a new reference
          next.delete(strategy);
          return next as Removing2faMethodsMap;
        });
        flashMessagesContext.showMessage({
          severity: "error",
          message: t("messages.generic_save_error.message"),
        });
      });
  }

  React.useEffect(() => {
    const fetchStaffUser = async () => {
      try {
        const { data } = await query({
          variables: { id: staffId },
        });
        if (data?.staff) {
          setStaffUser(data.staff);
        } else {
          console.error("Staff user not found");
        }
      } catch (error) {
        console.error("Error fetching staff user:", error);
      }
    };

    fetchStaffUser();
  }, [staffId]);

  if (!staffUser) {
    return null; // or a loading indicator
  }

  const verifiedMfaMethods = staffUser?.user?.mfaMethods.filter((i) => {
    const currentState = removing2faMethodsState.get(
      i.strategy as unknown as MfaCodeStrategy,
    );
    return i.verified && currentState !== Remove2faMethodState.Removed;
  });

  const cardSlot = !!verifiedMfaMethods?.length ? (
    <Card title="MFA Methods">
      <FlatActionList
        items={verifiedMfaMethods.map((method, i) => ({
          key: method.identifier || `method-${i}`,
          title: `Remove ${method.identifier || "******"}`,
          subTitle: method.strategy,
          destructive: true,
          disabled: removing2faMethodsState.has(
            method.strategy as unknown as MfaCodeStrategy,
          ),
          onPress: () => {
            handleRemove2faMethodForStaffUser(
              method.strategy as unknown as MfaCodeStrategy,
            );
          },
        }))}
      />
    </Card>
  ) : null;

  return (
    <StaffUserForm
      defaultFormValues={{
        id: staffUser.id,
        fullName: staffUser.fullName,
        initials: staffUser.initials,
        emailAddress: staffUser.emailAddress,
        username: staffUser.username,
        newPassword: "",
        confirmPassword: "",
        enabled: staffUser.enabled ?? true, // defaults to true if not set
      }}
    >
      <Content {...props} cardSlot={cardSlot} />
    </StaffUserForm>
  );
};
